import { defineComponent as _defineComponent } from 'vue'
import {
    StatusFiltersValues,
    StatusFiltersLabels,
  } from "@/constants/statusFilters";
  import { computed, onMounted, onUnmounted, ref, watch } from "vue";
  import ConfirmDeleteModal from "@/components/ui/modals/ConfirmDeleteModal.vue";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import CreateLeadModal from "@/v2/components/ui/modals/CreateLeadModal.vue";
  import StatusModal from "@/v2/components/ui/modals/StatusModal.vue";
  import BulkStatusModal from "@/v2/components/ui/modals/BulkStatusModal.vue";
  import SourceModal from "@/v2/components/ui/modals/SourceModal.vue";
  import router from "@/router";
  import store from "@/store";
  import { getLeadStatusClass, leadStatuses } from "@/v2/helpers/lead-status";
  import plural from "@/features/plural";
  import { debounce } from "@/helpers/debounce";
  import { LeadInterface } from "@/types/LeadInterface";
  import { POSITION, useToast } from "vue-toastification";
  import { LeadStatus } from "@/v2/enums/LeadStatus";
  import { isValidFileSize, isXlsxType } from "@/helpers/fileUpload";
  import { formatDate } from "@/helpers/date";
  import LeadSourceEnum from "@/enums/LeadSourceEnum";
  import { EstateInterface } from "@/types/EstateInterface";
  import { useRequest } from "@/features/useRequest";
  import { EstateActions } from "@/store/modules/estate";
  import { isCollaborator } from "@/helpers/estate/isCollaborator";

  interface LeadListItem {
    id: string;
    selected: boolean;
    status: LeadStatus;
  }

  type LeadFilterItem =
    | { value: StatusFiltersValues; label: StatusFiltersLabels }
    | { value: LeadStatus; label: LeadStatus };

  
export default /*@__PURE__*/_defineComponent({
  __name: 'Leads',
  setup(__props, { expose: __expose }) {
  __expose();

  const toast = useToast();
  const filters = leadStatuses;
  const activeFilter = ref<LeadFilterItem>(leadStatuses[0]);
  const searchQuery = ref("");
  const debounceSearch = debounce(getLeads);
  const estateId = computed(() => {
    return store.state.user.selectedEstateId;
  });
  const leads = ref<LeadInterface[]>([]);
  const leadList = ref<LeadListItem[]>([]);
  const isSelectAll = ref(false);
  const selectedLeadsList = computed(() => {
    return leadList.value.filter((item) => item.selected);
  });
  const showCreateModal = ref(false);
  const showConfirmBulkDeleteModal = ref(false);
  const isInvitingToWaitlist = ref(false);
  const isExportingLeads = ref(false);
  const estate = ref<EstateInterface | null>(null);
  const showStatusModal = ref(false);
  const showBulkStatusModal = ref(false);
  const selectedLead = ref<LeadInterface | null>(null);
  const windowWidth = ref(window.innerWidth);
  const resizeHandler = ref<((event: Event) => void) | null>(null);
  const editingField = ref<string>("");
  const editValues = ref<Record<string, string>>({});
  const showSourceModal = ref(false);
  const editingLead = ref<LeadInterface | null>(null);

  const isEmpty = computed(() => !loading.value && leads.value.length === 0);
  const isMobile = computed(() => windowWidth.value < 850);

  const isEstateCollaborator = computed(() => {
    if (!estate.value) return false;

    return isCollaborator(estate.value);
  });

  const getEstate = async () => {
    const { handleRequestWithLoading } = useRequest(
      undefined,
      undefined,
      POSITION.TOP_LEFT
    );
    const res = await handleRequestWithLoading<EstateInterface, string>(
      EstateActions.GET_ESTATE,
      estateId.value,
      loading
    );

    estate.value = res?.data;
  };

  const templateDownloadLink = ref(null);

  function changeFilter(item: LeadFilterItem) {
    activeFilter.value = item;
    getLeads();
  }

  const loading = ref(false);

  function getLeads() {
    if (estateId.value) {
      loading.value = true;
      store
        .dispatch("contact/getLeads", {
          estateId: estateId.value,
          query: searchQuery.value,
          status:
            activeFilter.value.value === StatusFiltersValues.ANY
              ? ""
              : activeFilter.value.value,
        })
        .then((res) => {
          leads.value = res.data;
          leadList.value = leads.value.map((item) => {
            return {
              id: item._id,
              selected: false,
              status: item.status,
            };
          });
          isSelectAll.value = false;
        })
        .finally(() => {
          loading.value = false;
        });
    }
  }

  function selectLead(checked: boolean, index: number) {
    leadList.value[index].selected = checked;
    isSelectAll.value =
      selectedLeadsList.value.length === leadList.value.length &&
      leadList.value.length > 0;
  }

  function selectAll(isSelect: boolean) {
    isSelectAll.value = isSelect;
    leadList.value.forEach((item) => {
      item.selected = isSelect;
    });
  }

  function inviteToWaitlist() {
    isInvitingToWaitlist.value = true;
    store
      .dispatch("subscription/inviteLeadToWaitlist", {
        estateId: estateId.value,
        leadIds: selectedLeadsList.value.map((item) => item.id),
        isV2: true,
      })
      .then(() => {
        toast.success("Invitations sent successfully");
      })
      .catch(() => {
        toast.error("Something went wrong");
      })
      .finally(() => {
        isInvitingToWaitlist.value = false;
      });
  }

  function bulkDeleteLeads() {
    store
      .dispatch("contact/bulkDeleteLeads", {
        estateId: estateId.value,
        leadIds: selectedLeadsList.value.map((item) => item.id),
      })
      .then(() => {
        getLeads();
      })
      .catch(() => {
        toast.error("Something went wrong");
      });
  }

  function exportLeads() {
    if (!estateId.value) {
      toast.error("Estate ID not found");
      return;
    }

    isExportingLeads.value = true;

    store
      .dispatch("contact/exportLeads", estateId.value)
      .then((response) => {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = url;
        link.download = `leads-export-${
          new Date().toISOString().split("T")[0]
        }.xlsx`;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("Leads exported successfully");
      })
      .catch((err) => {
        console.error("Error exporting leads:", err);

        if (err.response?.data?.message) {
          toast.error(err.response.data.message);
        } else {
          toast.error("Failed to export leads. Please try again.");
        }
      })
      .finally(() => {
        isExportingLeads.value = false;
      });
  }

  function uploadLeads(e: Event) {
    const target = e.target as HTMLInputElement;

    if (!target.files?.length) {
      return;
    }

    const selectedFile = target.files[0];

    if (!isXlsxType(selectedFile) || !isValidFileSize(selectedFile)) {
      return;
    }

    const data = new FormData();

    data.append("file", selectedFile as File);
    store
      .dispatch("contact/importLeads", { estateId: estateId.value, data })
      .then(() => {
        toast.success("File successfully uploaded");
        getLeads();
      })
      .catch((err) => {
        if (err.response.data?.message) {
          toast.error(err.response.data.message);
        } else {
          toast.error("Something went wrong");
        }
      });
  }

  function getTemplateLink() {
    store
      .dispatch("contact/downloadLeadsTemplate")
      .then(
        (res) =>
          (templateDownloadLink.value = res.data.message ? null : res.data)
      );
  }

  const openStatusModal = (lead: LeadInterface) => {
    selectedLead.value = lead;
    showStatusModal.value = true;
  };

  const closeStatusModal = () => {
    showStatusModal.value = false;
    selectedLead.value = null;
  };

  const openBulkStatusModal = () => {
    showBulkStatusModal.value = true;
  };

  const closeBulkStatusModal = () => {
    showBulkStatusModal.value = false;
  };

  const startEdit = (lead: LeadInterface, field: string) => {
    const editKey = `${lead._id}-${field}`;

    if (editingField.value === editKey) {
      return;
    }

    editingField.value = editKey;

    if (field === "name") {
      editValues.value[editKey] = `${lead.firstName || ""} ${
        lead.lastName || ""
      }`.trim();
    } else if (field === "email") {
      editValues.value[editKey] = lead.email || "";
    } else if (field === "phone") {
      editValues.value[editKey] = lead.phone || "";
    } else if (field === "notes") {
      editValues.value[editKey] = lead.notes || "";
    }
  };

  const cancelEdit = () => {
    editingField.value = "";
    editValues.value = {};
  };

  const saveField = (lead: LeadInterface, field: string) => {
    const editKey = `${lead._id}-${field}`;
    const newValue = editValues.value[editKey];

    if (!newValue || newValue.trim() === "") {
      cancelEdit();
      return;
    }

    let updateData: Partial<LeadInterface> = {};

    if (field === "name") {
      const nameParts = newValue.trim().split(" ");

      updateData.firstName = nameParts[0] || "";
      updateData.lastName = nameParts.slice(1).join(" ") || "";
    } else if (field === "email") {
      updateData.email = newValue.trim();
    } else if (field === "phone") {
      updateData.phone = newValue.trim();
    } else if (field === "notes") {
      updateData.notes = newValue.trim();
    }

    store
      .dispatch("contact/updateLead", {
        id: lead._id,
        data: updateData,
      })
      .then(() => {
        const leadIndex = leads.value.findIndex((l) => l._id === lead._id);

        if (leadIndex !== -1) {
          leads.value[leadIndex] = { ...leads.value[leadIndex], ...updateData };
        }

        cancelEdit();
        toast.success(
          `${
            field.charAt(0).toUpperCase() + field.slice(1)
          } updated successfully`
        );
      })
      .catch(() => {
        toast.error("Something went wrong");
        cancelEdit();
      });
  };

  const openSourceModal = (lead: LeadInterface) => {
    editingLead.value = lead;
    showSourceModal.value = true;
  };

  const closeSourceModal = () => {
    showSourceModal.value = false;
    editingLead.value = null;
  };

  const handleSourceUpdate = (source: string) => {
    if (editingLead.value) {
      store
        .dispatch("contact/updateLead", {
          id: editingLead.value._id,
          data: { source },
        })
        .then(() => {
          const leadIndex = leads.value.findIndex(
            (l) => l._id === editingLead.value?._id
          );

          if (leadIndex !== -1) {
            leads.value[leadIndex] = { ...leads.value[leadIndex], source };
          }

          closeSourceModal();
          toast.success("Source updated successfully");
        })
        .catch(() => {
          toast.error("Something went wrong");
        });
    }
  };

  const showCreatedAtNotification = () => {
    toast.info("Creation date can't be modified", {
      position: POSITION.TOP_CENTER,
      timeout: 3000,
    });
  };

  const handleStatusUpdate = (newStatus: LeadStatus) => {
    if (selectedLead.value) {
      store
        .dispatch("contact/bulkChangeLeadsStatuses", {
          estateId: estateId.value,
          leadIds: [selectedLead.value._id],
          status: newStatus,
        })
        .then(() => {
          const leadIndex = leads.value.findIndex(
            (l) => l._id === selectedLead.value?._id
          );

          if (leadIndex !== -1) {
            leads.value[leadIndex] = {
              ...leads.value[leadIndex],
              status: newStatus,
            };
            const leadListIndex = leadList.value.findIndex(
              (l) => l.id === selectedLead.value?._id
            );

            if (leadListIndex !== -1) {
              leadList.value[leadListIndex].status = newStatus;
            }
          }

          closeStatusModal();
          toast.success("Status updated successfully");
        })
        .catch(() => {
          toast.error("Something went wrong");
        });
    }
  };

  const handleBulkStatusUpdate = (newStatus: LeadStatus) => {
    const updatedLeadIds = selectedLeadsList.value.map((item) => item.id);

    store
      .dispatch("contact/bulkChangeLeadsStatuses", {
        estateId: estateId.value,
        leadIds: updatedLeadIds,
        status: newStatus,
      })
      .then(() => {
        updatedLeadIds.forEach((leadId) => {
          const leadIndex = leads.value.findIndex((l) => l._id === leadId);

          if (leadIndex !== -1) {
            leads.value[leadIndex] = {
              ...leads.value[leadIndex],
              status: newStatus,
            };
          }

          const leadListIndex = leadList.value.findIndex(
            (l) => l.id === leadId
          );

          if (leadListIndex !== -1) {
            leadList.value[leadListIndex].status = newStatus;
          }
        });
        closeBulkStatusModal();
        toast.success("Statuses updated successfully");
      })
      .catch(() => {
        toast.error("Something went wrong");
      });
  };

  onMounted(() => {
    getLeads();
    getEstate();
    getTemplateLink();

    const handleResize = () => {
      windowWidth.value = window.innerWidth;
    };

    resizeHandler.value = handleResize;
    window.addEventListener("resize", handleResize);
  });

  onUnmounted(() => {
    if (resizeHandler.value) {
      window.removeEventListener("resize", resizeHandler.value);
      resizeHandler.value = null;
    }
  });

  watch(
    () => estateId.value,
    () => {
      getLeads();
      getEstate();
    }
  );

  watch(
    () => searchQuery.value,
    () => {
      debounceSearch();
    }
  );

const __returned__ = { toast, filters, activeFilter, searchQuery, debounceSearch, estateId, leads, leadList, isSelectAll, selectedLeadsList, showCreateModal, showConfirmBulkDeleteModal, isInvitingToWaitlist, isExportingLeads, estate, showStatusModal, showBulkStatusModal, selectedLead, windowWidth, resizeHandler, editingField, editValues, showSourceModal, editingLead, isEmpty, isMobile, isEstateCollaborator, getEstate, templateDownloadLink, changeFilter, loading, getLeads, selectLead, selectAll, inviteToWaitlist, bulkDeleteLeads, exportLeads, uploadLeads, getTemplateLink, openStatusModal, closeStatusModal, openBulkStatusModal, closeBulkStatusModal, startEdit, cancelEdit, saveField, openSourceModal, closeSourceModal, handleSourceUpdate, showCreatedAtNotification, handleStatusUpdate, handleBulkStatusUpdate, get StatusFiltersValues() { return StatusFiltersValues }, get StatusFiltersLabels() { return StatusFiltersLabels }, computed, onMounted, onUnmounted, ref, watch, ConfirmDeleteModal, DropDown, CreateLeadModal, StatusModal, BulkStatusModal, SourceModal, get router() { return router }, get store() { return store }, get getLeadStatusClass() { return getLeadStatusClass }, get leadStatuses() { return leadStatuses }, get plural() { return plural }, get debounce() { return debounce }, get LeadInterface() { return LeadInterface }, get POSITION() { return POSITION }, get useToast() { return useToast }, get LeadStatus() { return LeadStatus }, get isValidFileSize() { return isValidFileSize }, get isXlsxType() { return isXlsxType }, get formatDate() { return formatDate }, get LeadSourceEnum() { return LeadSourceEnum }, get EstateInterface() { return EstateInterface }, get useRequest() { return useRequest }, get EstateActions() { return EstateActions }, get isCollaborator() { return isCollaborator } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})